<template>
  <div class="relative">
    <svg ref="graph" :class="{ allLabels }" id="g">
      <defs>
        <filter id="blur" x="-100%" y="-100%" width="300%" height="300%">
           <feGaussianBlur stdDeviation="16" />
        </filter>
      </defs>
    </svg>
    <div class="absolute top-0 left-1/2 w-full transform -translate-x-1/2 py-1 md:py-2 px-2 lg:px-4 pointer-events-none">
      <div class="max-w-6xl mx-auto flex justify-end">
        <div class="w-8 text-center text-3xl font-thin pointer-events-auto m-1 mt-3">
          <button @click="zoomIn" :class="{ 'opacity-25': noZoomIn }" class="bg-white mb-1 leading-tight hover:bg-gray-100 block w-full rounded-t-full cursor-pointer shadow transition-opacity duration-150 focus:outline-none border-b focus:border-black">
            &plus;
          </button>
          <button @click="zoomOut" :class="{ 'opacity-25': noZoomOut }" class="bg-white leading-tight hover:bg-gray-100 block w-full rounded-b-full cursor-pointer shadow transition-opacity duration-150 focus:outline-none border-t focus:border-black">
            -
          </button>
        </div>
      </div>
    </div>

    <div class="hidden md:block absolute left-0 right-0 bottom-0 h-20 bg-white-fade"></div>
  </div>
</template>

<script>
  import {
    event,
    forceCenter,
    forceCollide,
    forceLink,
    forceManyBody,
    forceSimulation,
    forceY,
    json,
    map,
    select,
    selectAll,
    voronoi,
    zoom
  } from 'd3'
  import debounce from 'debounce'

  export default {
    name: 'Graph',
    data () {
      return {
        svg: null,
        g: null,
        simulation: null,
        graphData: null,

        zoom: null,
        zoomScale: 1.15,

        width: window.innerWidth,
        height: window.innerHeight
      }
    },
    computed: {
      allLabels () {
        return this.zoomScale > 1.7
      },
      noZoomOut () {
        return this.zoomScale < 1.15
      },
      noZoomIn () {
        return this.zoomScale > 2
      },
      activeNode () {
        if (!this.$route.name || !this.$route.name.includes('posts-')) return false
        const { category, tag } = this.$route.params
        if (!category && !tag) return false
        return category || tag
      }
    },
    methods: {
      graphSetup () {
        this.svg = select('#g')
        this.g = this.svg.append('g')
      },
      async buildGraph (refresh = false) {
        // load it
        let graph = this.graphData ? cloneObj(this.graphData) : null
        if (!graph) {
          // console.time('get-graph')
          graph = await json('/wp-json/gkb/v1/graph')
          // console.timeEnd('get-graph')
          this.graphData = cloneObj(graph)
        }

        const sizeConst = Math.min(window.innerWidth / 20, 23)
        const svg = this.svg

        if (graph) {
          this.g.remove()
          this.g = svg.append('g')
        }

        const g = this.g
        const el = this.$refs.graph

        const width = el.clientWidth
        const height = el.clientHeight

        const vo = voronoi()
          .x((d) => d.x)
          .y((d) => d.y)
          .extent([[-200, -200], [width + 200, height + 200]])

        const simulation = forceSimulation()
          .force('link', forceLink().id((d) => d.id).strength((link) => {
            return link.source.group === link.source.target ? 0.2 : 0.05
          })).force('charge', forceManyBody().strength(-400))
          .force('forceY', sizeConst > 33 ? forceY().strength(0.1).y(0.5) : null)
          .force('center', forceCenter(width / 2, (height / 2) + 50))

        this.simulation = simulation

        this.zoom = zoom()
          .extent([[0, 0], [width, height]])
          .scaleExtent([0.8, 2.2])
          .on('zoom', () => {
            g.attr('transform', event.transform)
            const z = event.transform.k

            const box = g.node().getBBox()
            const topLeft = [box.x, box.y]
            const bottomRight = [
              box.x + Math.max(box.width, window.innerWidth),
              box.y + Math.max(box.height, window.innerHeight)
            ]

            selectAll('text').attr('transform', (d) => `scale(${1 / z})`)
            selectAll('.dot').attr('transform', (d) => `scale(${1 / z})`)
            this.zoomScale = z
            this.zoom.translateExtent([topLeft, bottomRight])
          })

        const groupSimulation = forceSimulation()
          .force('link', forceLink().id((d) => d.id).strength((link) => {
            return link.value * 2
          }))
          .force('charge', forceManyBody().strength(-100))
          .force('center', forceCenter(width / 2, height / 2))

        const visNodes = graph.nodes.filter((n) => {
          if (window.innerWidth > 768) return true
          return n.taxonomy === 'category'
        })
        const ids = visNodes.map(n => n.id)
        const visLinks = graph.links.filter((l) => {
          return ids.includes(l.source) && ids.includes(l.target)
        })

        // Get the data to make a force network of the groups:
        let groups = []

        visNodes.forEach((d) => {
          if (groups.indexOf(d.group) === -1) groups.push({ id: d.group })
        })

        let groupLinks = {}

        visLinks.forEach((d) => {
          let nodes = visNodes

          let d3map = map(nodes, (d) => d.id)

          let target = d.target
          let source = d.source

          let groupTarget = null
          let groupSource = null

          // ensure links targets and sources are ordered the same way:
          if (d3map.get(target).group > d3map.get(source).group) {
            groupTarget = d3map.get(target).group
            groupSource = d3map.get(source).group
          } else {
            groupTarget = d3map.get(source).group
            groupSource = d3map.get(target).group
          }

          // get the links between each group:
          if (groupTarget !== groupSource) {
            const property = '_' + groupSource + '-' + groupTarget

            if (groupLinks[property]) {
              groupLinks[property]++
            } else {
              groupLinks[property] = 1
            }
          }
        })

        const groupGraph = {}

        groupGraph.nodes = groups
        groupGraph.links = []

        for (const link in groupLinks) {
          const parts = link.substring(1).split('-')
          const source = parts[0]
          const target = parts[1]
          const value = groupLinks[link]

          groupGraph.links.push({ source: source, target: target, value: value })
        }

        // start the group simulation
        groupSimulation.nodes(groupGraph.nodes)

        // append nodes, in blurred layer
        const node = g.append('g')
          .attr('class', 'nodes blur-layer')
          .selectAll('g')
          .data(visNodes)
          .enter().append('g')

        node.append('circle')
          .attr('r', (d) => Math.log1p(Math.max(10, d.count)) * sizeConst)
          .attr('class', (d) => {
            return d.taxonomy === 'category' ? 'bigspace' : 'bigcircle'
          })
          .attr('fill', (d) => {
            return graph.colors[d.group || d.term_id]
          })

        // append the labels, in non-blurred layer
        const texts = g.append('g')
          .attr('class', 'texts')
          .selectAll('g')
          .data(visNodes)
          .enter().append('g')
          .on('click', this.filterByTag)
          .attr('id', (d) => `label-${d.id}`)
          .attr('class', (d) => {
            const p = 'cursor-pointer'
            const s = d.count > 5 ? ' show' : ''
            let classes = d.taxonomy === 'category' ? `${p} category` : `${p} tag${s}`
            if (this.activeNode === d.id) {
              classes += ' focussed'
            }
            return classes
          })

        texts.append('circle')
          .attr('r', 2)
          .attr('fill', 'transparent')
          .attr('stroke', 'black')
          .attr('class', 'dot')

        texts.append('text')
          .attr('dx', 0)
          .attr('dy', 2)
          .attr('fill', (d) => {
            if (d.taxonomy === 'category') {
              return graph.colors[d.term_id]
            }
          })
          .text(d => d.name)

        // main simulation:
        this.simulation.alpha(1)
        this.simulation.nodes(visNodes).on('tick', ticked)

        this.simulation.force('link').links(visLinks)
        // keep them apart...
        this.simulation.force('collide', forceCollide(collisionCalc))

        // set up the vo, in .nodes layer
        let cells = g.select('.nodes')
          .append('g')
          .selectAll()
          .data(this.simulation.nodes())
          .enter().append('g')
          .attr('fill', (d) => graph.colors[d.group || d.term_id])
          .attr('class', 'voronoi')

        let cell = cells.append('path').data(vo.polygons(this.simulation.nodes()))

        // zoom stuff here?
        svg.call(this.zoom)
          .on('wheel.zoom', null)

        this.zoom.scaleTo(svg, this.zoomScale)

        // skip a few, then freeze
        this.simulation.tick(1000)

        // Tick function:
        function ticked () {
          let alpha = this.alpha()

          let centroids = {}

          // get the centroid of each group based on the groupSimulation
          groupSimulation.nodes().forEach((d) => {
            let cx = d.x
            let cy = d.y
            centroids[d.id] = { x: cx, y: cy }
          })

          // minimum distance to apply a correction:
          let minDistance = 50
          if (alpha < 0.2) {
            minDistance = 50 + (1000 * (0.1 - alpha))
          }

          // apply a minor correction to each node if needed:
          node.each((d) => {
            let cx = centroids[d.group].x
            let cy = centroids[d.group].y
            let x = d.x
            let y = d.y
            let dx = cx - x
            let dy = cy - y

            let r = Math.sqrt(dx * dx + dy * dy)

            if (r > minDistance) {
              d.x = x * 0.95 + cx * 0.05
              d.y = y * 0.95 + cy * 0.05
            }
          })

          texts.each((d) => {
            let cx = centroids[d.group].x
            let cy = centroids[d.group].y
            let x = d.x
            let y = d.y
            let dx = cx - x
            let dy = cy - y

            let r = Math.sqrt(dx * dx + dy * dy)

            if (r > minDistance) {
              d.x = x * 0.95 + cx * 0.05
              d.y = y * 0.95 + cy * 0.05
            }
          })

          // update voronoi:
          cell = cell.data(vo.polygons(simulation.nodes())).attr('d', renderCell)

          // update nodes:
          node.attr('transform', (d) => {
            return `translate(${d.x}, ${d.y})`
          })
          texts.attr('transform', (d) => {
            return `translate(${d.x}, ${d.y})`
          })
        }

        function renderCell (d) {
          if (!d) return null
          return d === null ? null : 'M' + d.join('L') + 'Z'
        }

        function collisionCalc ({ count }) {
          return Math.log1p(Math.max(2, count)) * sizeConst
        }
      },
      zoomIn () {
        this.svg.transition().call(this.zoom.scaleBy, 1.3)
      },
      zoomOut () {
        this.svg.transition().call(this.zoom.scaleBy, 1 / 1.3)
      },

      filterByTag ({ id, taxonomy }) {
        const base = taxonomy === 'category' ? 'sammlung' : 'tag'
        this.$router.push(`/${base}/${id}`).catch(() => {})
      },

      bounceGraph: debounce(function () {
        if (this.width === window.innerWidth) return

        this.width = window.innerWidth
        this.height = window.innerHeight
        this.buildGraph(true)
      }, 600),

      nodeFocus (slug) {
        this.g.select(`#label-${slug}`)
          .classed('focussed', true)
      },
      nodeBlur (slug) {
        this.g.select(`#label-${slug}`)
          .classed('focussed', false)
      }
    },
    watch: {
      activeNode (newVal, oldVal) {
        setTimeout(() => {
          oldVal && this.nodeBlur(oldVal)
          newVal && this.nodeFocus(newVal)
        }, 10)
      }
    },
    mounted () {
      this.graphSetup()
      this.buildGraph()
      window.addEventListener('resize', this.bounceGraph)
    },
    destroyed () {
      window.removeEventListener('resize', this.bounceGraph)
    }
  }

  function cloneObj (data) {
    return JSON.parse(JSON.stringify(data))
  }
</script>

<style>
  #g, .g-spacer {
    @apply w-screen;
    height: theme('height.fill');
    height: calc(100vh - var(--offset, 208px));
    min-height: 0;
  }

  #g { @apply cursor-move; }

  #g .nodes circle {
    stroke: #000;
    stroke-width: 1px;
  }

  #g .nodes text,
  #g .texts text {
    @apply font-thin;
    font-size: 13px;
    opacity: 0;
    transition: opacity .2s;
    text-anchor: middle;
    alignment-baseline: middle;
  }

  #g .tag:hover text,
  #g .tag.show text,
  #g.allLabels .nodes text,
  #g.allLabels .texts text,
  #g .category text,
  #g .focussed text {
    opacity: 1;
  }

  #g .focussed text {
    @apply font-medium;
    fill: theme('colors.black');
    font-size: 19px !important;
  }

  #g .tag:hover .dot,
  #g .tag.show .dot,
  #g.allLabels .nodes .dot,
  #g.allLabels .texts .dot,
  #g .focussed .dot {
    opacity: 0;
  }

  #g .category circle {
    display: none;
  }

  #g .category text {
    @apply font-medium;
    font-size: 90%;
  }

  #g .bigcircle {
    stroke-width: 0 !important;
    opacity: .3;
  }

  #g .bigspace { opacity: .2; }

  @screen md {
    #g .bigspace { opacity: 0; }
  }

  #g path {
    opacity: .4;
    stroke-width: 0px;
    pointer-events: none;
  }

  #g .voronoi path { opacity: .2; }

  #g .blur-layer {
    filter: blur(16px);
    filter: url('#blur');
  }
</style>
